import { Outlet, useLoaderData } from '@remix-run/react'
import type { LoaderFunctionArgs } from '@remix-run/server-runtime'
import { json } from '@remix-run/server-runtime'
import { AnimatePresence } from 'motion/react'

import { ShopFooter } from '~/components/ShopLayout'
import {
  CollectionsReorderDrawer,
  ProductsReorderDrawer,
  ShopReorderProvider,
} from '~/components/ShopReorder'
import { listAttachmentsOfTarget } from '~/models/attachment.server'
import { recordVisit } from '~/models/event.server'
import { getShopMetadata } from '~/models/shop.server'
import { getShopAnalyticByShopId } from '~/models/shop_analytic.server'
import { ShopAnalytics } from '~/routes/shop.$shopSlug._layout/components/ShopAnalytics'
import { ShopHero } from '~/routes/shop.$shopSlug._layout/components/ShopHero'
import { ShopNavbar } from '~/routes/shop.$shopSlug._layout/components/ShopNavbar'
import { requireShopAndItsOptionalSeller } from '~/services/auth-shop.server'
import { getApexSession } from '~/services/session-apex.server'

export const loader = async (loaderArgs: LoaderFunctionArgs) => {
  const { shop, seller } = await requireShopAndItsOptionalSeller(
    loaderArgs.request
  )

  const [
    sellerSession,
    { productsCount, collectionsCount },
    shopSliderAttachments,
    shopAnalytics,
  ] = await Promise.all([
    getApexSession(loaderArgs.request),

    getShopMetadata(shop.id),

    listAttachmentsOfTarget(shop, {
      targetId: null,
      targetType: 'slider',
      page: 1,
      limit: 30,
      status: 'live',
      sortBy: 'disposition',
      sortDir: 'desc',
    }),

    seller ? getShopAnalyticByShopId(shop.id) : Promise.resolve(undefined),

    !seller
      ? recordVisit(shop.id, loaderArgs.request.url)
      : Promise.resolve(undefined),
  ])

  const analyticsEnabled = sellerSession.get('analyticsEnabled')

  let slider = null
  if (seller || shopSliderAttachments.attachments.length > 0) {
    slider = {
      attachments: shopSliderAttachments.attachments,
    }
  }

  return json({
    shopAnalytics,
    analyticsEnabled,
    slider,
    navigation: {
      counts: {
        products: productsCount,
        collections: collectionsCount,
      } as const,
    },
  })
}

export default function ShopLayout() {
  const { analyticsEnabled, shopAnalytics, slider, navigation } =
    useLoaderData<typeof loader>()

  return (
    <ShopReorderProvider>
      <AnimatePresence>
        {analyticsEnabled && <ShopAnalytics shopAnalytics={shopAnalytics} />}
      </AnimatePresence>
      <div className="bg-background relative flex min-h-full flex-col rounded-t-[inherit]">
        <div className="flex flex-1 flex-col gap-2 px-2 @sm:gap-4 @sm:px-6 @sm:pt-6">
          <ShopHero slider={slider} />
          <ShopNavbar counts={navigation.counts} />
          <Outlet />
        </div>
        <ShopFooter />
        <ProductsReorderDrawer />
        <CollectionsReorderDrawer />
      </div>
    </ShopReorderProvider>
  )
}
